import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY, from, Observable, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { HttpEvent, HttpInterceptorFn, HttpHandlerFn, HttpRequest, HttpErrorResponse } from '@angular/common/http';

import { reportHttpServerErrorToFaro } from '../observability/http-server-error.util';
import { AuthService } from '../services/auth.service';
import { KioskRouteService } from '../services/kiosk-route.service';
import { DeviceSessionService } from '../services/device-session.service';
import { DEVICE_POLICY_HTTP_HEADER } from '../constants/device-session.constants';

const BEARER_EXCLUDED_URLS = ['/auth/refresh-token', '/auth/sign-in', '/auth/kiosk/sign-in'];

/**
 * An HTTP interceptor function used for appending an authorization header to outgoing HTTP requests,
 * refreshing expired access tokens, and handling authentication-related errors.
 *
 * This interceptor checks a list of excluded URLs (`EXCLUDED_URLS`) to bypass authentication for specific requests.
 * For requests requiring authentication, it gets the access token from the `AuthService`. If the token is expired,
 * it attempts to refresh the token using the `AuthService`. On a successful token refresh, the request is retried with
 * the updated token. If the token refresh fails or an authentication error (e.g., 401) occurs, the error is handled
 * using a specific error handler.
 *
 * Additionally, the interceptor handles 403 (Forbidden) and 404 (Not Found) errors by redirecting users to appropriate
 * error pages with skipCanLeave flag to bypass any navigation guards.
 *
 * The interceptor leverages reactive programming to seamlessly manage token expiration and error handling while ensuring
 * the request flow continues or fails gracefully.
 *
 * @param req The HTTP request made by the client.
 * @param next The next handler in the HTTP pipeline.
 * @returns An `Observable` of the HTTP event emitted by the HTTP pipeline, including handling of token refresh and errors.
 */
export const authInterceptor: HttpInterceptorFn = <T>(req: HttpRequest<T>, next: HttpHandlerFn): Observable<HttpEvent<T>> => {
	const router = inject(Router);
	const authService = inject(AuthService);
	const deviceSessionService = inject(DeviceSessionService);
	const kioskRouteService = inject(KioskRouteService);

	const skipBearerAuth = BEARER_EXCLUDED_URLS.some(url => req.url.includes(url));

	if (skipBearerAuth) {
		const deviceReq = addAuthHeader(req, authService, deviceSessionService, true);
		return next(deviceReq).pipe(map(event => event as HttpEvent<T>));
	}

	const accessToken = authService.getAccessToken();
	if (accessToken && authService.isTokenExpired()) {
		return authService.refreshToken().pipe(
			switchMap(() => {
				const authReq = addAuthHeader(req, authService, deviceSessionService);
				return next(authReq).pipe(
					map(event => event as HttpEvent<T>),
					catchError((error: HttpErrorResponse) => {
						if (error.status === 403) {
							return handleForbiddenError<T>(router, error);
						}
						if (error.status === 404) {
							return handleNotFoundError<T>(router, error);
						}
						reportHttpServerErrorToFaro(error);
						return throwError(() => error);
					})
				);
			}),
			catchError(error => handleAuthError<T>(authService, router, kioskRouteService, error))
		);
	}

	const authReq = addAuthHeader(req, authService, deviceSessionService);

	return next(authReq).pipe(
		map(event => event as HttpEvent<T>),
		catchError((error: HttpErrorResponse) => {
			if (error.status === 401 && accessToken) {
				return authService.refreshToken().pipe(
					switchMap(() => {
						const retryReq = addAuthHeader(req, authService, deviceSessionService);
						return next(retryReq).pipe(
							map(event => event as HttpEvent<T>),
							catchError((retryError: HttpErrorResponse) => {
								if (retryError.status === 403) {
									return handleForbiddenError<T>(router, retryError);
								}
								if (retryError.status === 404) {
									return handleNotFoundError<T>(router, retryError);
								}
								reportHttpServerErrorToFaro(retryError);
								return throwError(() => retryError);
							})
						);
					}),
					catchError(err => handleAuthError<T>(authService, router, kioskRouteService, err))
				);
			}
			if (error.status === 403) {
				return handleForbiddenError<T>(router, error);
			}
			if (error.status === 404) {
				return handleNotFoundError<T>(router, error);
			}
			reportHttpServerErrorToFaro(error);
			return throwError(() => error);
		})
	);
};

/**
 * Adds authorization and active role headers to an HTTP request.
 *
 * This function augments the provided HTTP request by including an
 * `Authorization` header with a bearer token and an `X-Active-Role`
 * header based on the active role, retrieved from the given AuthService.
 * If no token or active role is available, the respective headers will not
 * be included in the request.
 *
 * @template T The type of the payload in the HTTP request.
 * @param {HttpRequest<T>} req The original HTTP request to which headers are added.
 * @param {AuthService} authService The authentication service used to retrieve the
 *                                   access token and active role information.
 * @param deviceSessionService - Device enrollment header source for warehouse clients.
 * @param {boolean} skipBearerAuth - When true, omits the Authorization header.
 * @returns {HttpRequest<T>} A new instance of the HTTP request with the additional
 *                           authorization-related headers.
 */
const addAuthHeader = <T>(
	req: HttpRequest<T>,
	authService: AuthService,
	deviceSessionService: DeviceSessionService,
	skipBearerAuth: boolean = false
): HttpRequest<T> => {
	const token = authService.getAccessToken();
	const activeRole = authService.getActiveRole();
	const deviceToken = deviceSessionService.getDeviceToken();

	const headers: Record<string, string> = {};

	if (token && !skipBearerAuth) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	if (activeRole && !skipBearerAuth) {
		headers['X-Active-Role'] = activeRole;
	}

	if (deviceToken) {
		headers[DEVICE_POLICY_HTTP_HEADER] = deviceToken;
	}

	return req.clone({ setHeaders: headers });
};

/**
 * Handles authentication errors encountered during HTTP requests.
 *
 * @template T The expected type of the HttpEvent.
 * @param {AuthService} authService - The authentication service used to manage user sessions.
 * @param {Router} router - The Angular Router used for navigation.
 * @param {KioskRouteService} kioskRouteService - Resolves the sign-in path for the current route namespace.
 * @param {HttpErrorResponse} error - The HTTP error response encountered during the request.
 * @returns {Observable<HttpEvent<T>>} An observable that either navigates the user to the sign-in page and completes, or rethrows the error.
 *
 * If the HTTP error status is 401 (Unauthorized), the user is signed out and redirected to the sign-in page.
 * For other error statuses, the error is rethrown as an observable error.
 */
const handleAuthError = <T>(
	authService: AuthService,
	router: Router,
	kioskRouteService: KioskRouteService,
	error: HttpErrorResponse
): Observable<HttpEvent<T>> => {
	if (error.status === 401) {
		authService.signOut();
		return from(router.navigateByUrl(kioskRouteService.getSignInPath())).pipe(switchMap(() => EMPTY));
	}
	return throwError(() => error);
};

/**
 * Handles forbidden (403) errors encountered during HTTP requests.
 *
 * @template T The expected type of the HttpEvent.
 * @param {Router} router - The Angular Router used for navigation.
 * @param {HttpErrorResponse} error - The HTTP error response containing the error message.
 * @returns {Observable<HttpEvent<T>>} An observable that navigates the user to the 403 error page and completes.
 *
 * When a 403 (Forbidden) error occurs, the user is redirected to the 403 error page
 * with the error message from the API response passed in the navigation state.
 * The navigation includes a skipCanLeave flag to bypass any canLeave guards.
 *
 * Special case: If rc flag is set in sessionStorage, the user is redirected
 * to / instead of the 403 error page (happens during role switching).
 */
const handleForbiddenError = <T>(router: Router, error: HttpErrorResponse): Observable<HttpEvent<T>> => {
	const lastUrl = sessionStorage.getItem('lastUrl') || window.location.href;
	const urlObj = new URL(lastUrl);
	const roleChanged = urlObj.searchParams.get('rc') === '1';

	if (roleChanged) {
		sessionStorage.removeItem('lastUrl');
		return from(router.navigate(['/'])).pipe(switchMap(() => EMPTY));
	}

	const errorMessage = error.error?.message || 'Insufficient permissions to perform this action';
	return from(
		router.navigate(['/error/403'], {
			state: {
				skipCanLeave: true,
				errorMessage: errorMessage
			}
		})
	).pipe(switchMap(() => EMPTY));
};

/**
 * Handles not found (404) errors encountered during HTTP requests.
 *
 * @template T The expected type of the HttpEvent.
 * @param {Router} router - The Angular Router used for navigation.
 * @param {HttpErrorResponse} error - The HTTP error response containing the error message.
 * @returns {Observable<HttpEvent<T>>} An observable that navigates the user to the 404 error page and completes.
 *
 * When a 404 (Not Found) error occurs, the user is redirected to the 404 error page
 * with the error message from the API response passed in the navigation state.
 * The navigation includes a skipCanLeave flag to bypass any canLeave guards.
 */
const handleNotFoundError = <T>(router: Router, error: HttpErrorResponse): Observable<HttpEvent<T>> => {
	const errorMessage = error.error?.message || 'The page you are looking for does not exist or has been moved.';
	return from(
		router.navigate(['/error/404'], {
			state: {
				skipCanLeave: true,
				errorMessage: errorMessage
			}
		})
	).pipe(switchMap(() => EMPTY));
};
